import { useSurveyI18n } from '../i18n';
import type { Figures, Season } from '../seasons';
import { fmtInt, fmtPct, share } from '../utils';
import { SeasonHead } from './Overview';

const COMPLETED_COLOR = '#0F75B8';
const NOT_COMPLETED_COLOR = '#B9E3EA';

/** started − completed, never below 0 (a result without its counter would make it negative). */
const notCompleted = (started: number | null, completed: number | null) => (started == null || completed == null ? null : Math.max(0, started - completed));

/**
 * Quizzes started vs completed (BE's metric, 2026-10-06): a quiz is started
 * when its session opens and uses an attempt; a completed one is a play.
 *
 * Top: the completion rate beside a 2 × 3 table — quizzes and players, each
 * started / completed / not completed — with each season's figure under the
 * scope's while both seasons are on show. Below: every season's weeks, one
 * column per season. Before the pipeline publishes the started counters those
 * figures read "—".
 */
export function CompletionCard({ figures, seasons }: { figures: Figures; seasons: Season[] }) {
  const { t } = useSurveyI18n();
  const measured = figures.quizzesStarted != null;
  const all = seasons.length > 1;
  const rows = [
    {
      label: t.quizzes,
      cells: (f: Figures) => [f.quizzesStarted, f.plays, notCompleted(f.quizzesStarted, f.plays)],
      tip: undefined,
    },
    {
      label: t.players,
      cells: (f: Figures) => [f.playersStarted, f.uniquePlayers, notCompleted(f.playersStarted, f.uniquePlayers)],
      tip: t.neverCompletedTip,
    },
  ];
  // "S1 412 318 · S2 168 400" under each figure.
  const perSeason = (cells: (f: Figures) => Array<number | null>, i: number) =>
    seasons.map((s) => `${t.seasonShort(s.n)} ${s.status === 'planned' ? '—' : fmtInt(cells(s.figures)[i])}`).join(' · ');

  return (
    <section className="su-card su-completion">
      <h2>{t.completionTitle}</h2>

      <div className="su-completion-summary">
        <div className="su-completion-rate">
          <div className="su-big">{fmtPct(share(figures.plays, figures.quizzesStarted))}</div>
          <span className="su-note">{t.completionRateNote}</span>
        </div>
        <table className="su-completion-matrix" aria-label={t.completionTitle}>
          <thead>
            <tr>
              <td />
              <th scope="col">{t.started}</th>
              <th scope="col">{t.completed}</th>
              <th scope="col">{t.notCompleted}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <th scope="row">{r.label}</th>
                {r.cells(figures).map((v, i) => (
                  <td key={i} data-tip={i === 2 ? r.tip : undefined}>
                    {fmtInt(v)}
                    {all && <span className="su-matrix-seasons">{perSeason(r.cells, i)}</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="su-completion-weeks">
        <div className="su-completion-weeks-head">
          <h3>{t.byWeek}</h3>
          <div className="su-legend">
            <span>
              <span className="su-swatch" style={{ background: COMPLETED_COLOR }} />
              {t.completed}
            </span>
            <span>
              <span className="su-swatch" style={{ background: NOT_COMPLETED_COLOR }} />
              {t.notCompleted}
            </span>
          </div>
        </div>
        <div className="su-col2">
          {seasons.map((s) => (
            <div key={s.id}>
              <SeasonHead season={s} detail={s.status === 'planned' ? '—' : t.completedPct(fmtPct(share(s.figures.plays, s.figures.quizzesStarted)))} />
              <div className="su-dist">
                {s.weeks.map((w, k) => {
                  const week = `${t.seasonShort(s.n)} · ${t.week(k + 1)}`;
                  const open = notCompleted(w.quizzesStarted, w.plays);
                  const pct = share(w.plays, w.quizzesStarted);
                  return (
                    <div className="su-dist-row" key={w.week}>
                      <span className={w.status === 'live' ? 'su-live-ink' : w.status === 'planned' ? 'su-muted' : ''}>{t.week(k + 1)}</span>
                      <div className="su-split short">
                        {w.status === 'planned' && <div className="empty">{t.notStartedYet}</div>}
                        {pct != null && w.quizzesStarted != null && w.quizzesStarted > 0 && (
                          <>
                            {w.plays != null && w.plays > 0 && (
                              <div
                                style={{ width: `${Math.min(100, pct)}%`, background: COMPLETED_COLOR }}
                                data-tip={t.completionTip(week, fmtInt(w.plays), fmtInt(w.quizzesStarted), fmtPct(pct))}
                                aria-label={t.completionTip(week, fmtInt(w.plays), fmtInt(w.quizzesStarted), fmtPct(pct))}
                              >
                                {pct >= 12 ? fmtPct(pct, 0) : ''}
                              </div>
                            )}
                            {open != null && open > 0 && (
                              <div
                                className="light"
                                style={{ width: `${100 - Math.min(100, pct)}%`, background: NOT_COMPLETED_COLOR }}
                                data-tip={t.notCompletedTip(week, fmtInt(open))}
                                aria-label={t.notCompletedTip(week, fmtInt(open))}
                              />
                            )}
                          </>
                        )}
                      </div>
                      <span>{pct == null ? '—' : t.completedPct(fmtPct(pct))}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="su-note">{measured ? t.completionNote : t.startedUnavailable}</p>
    </section>
  );
}
